import React, { useCallback, useEffect } from "react";
import {
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Row,
  Col,
  Switch,
  Tooltip,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getCustomers } from "../../store/actions/customers";
import { getAllSettings } from "../../store/actions/settings";
import { getSeries } from "../../store/actions/series";
import { getCategories } from "../../store/actions/category";   // adjust path if needed
import { getColors } from "../../store/actions/color";          // adjust path if needed
import { STORAGE_TYPE, RAM_TYPE } from "../../constants/data";

const ItemForm = ({ visible, onCreate, onCancel, record }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  // ── Selectors ──────────────────────────────────────────────────────────────
  const customers = useSelector((state) => state.customers.list);
  const settings = useSelector((state) => state.settings.list);
  const seriesList = useSelector((state) => state.series.list);
  const categories = useSelector((state) => state.category.list);
  const colors = useSelector((state) => state.color.list);

  // ── Fetch reference data on mount ──────────────────────────────────────────
  useEffect(() => {
    dispatch(getCustomers());
    dispatch(getSeries());
    dispatch(getCategories());
    dispatch(getColors());
  }, [dispatch]);

  // ── Populate / reset form when record changes ──────────────────────────────
  useEffect(() => {
    if (record?.id) {
      form.setFieldsValue({
        ...record,
        battery: record.battery ? Number(record.battery) : undefined,
        height: record.height ? Number(record.height) : undefined,
        length: record.length ? Number(record.length) : undefined,
        weight: record.weight ? Number(record.weight) : undefined,
        // IDs come straight from the backend — just pass them through
        seriesId: record.seriesId ?? undefined,
        categoryid: record.categoryid ?? undefined,
        colorId: record.colorId ?? undefined,
      });
    } else {
      form.resetFields();
    }
  }, [form, record, record?.id]);

  // ── Auto-calculate amount ──────────────────────────────────────────────────
  const handleValuesChange = useCallback(
    (changedValues, allValues) => {
      if (allValues?.type) {
        dispatch(getAllSettings());
        const setting = Array.isArray(settings) && settings.length > 0 ? settings[0] : null;
        const rate =
          allValues.type === "EWAY"
            ? setting?.ewayRatePerPcs
            : setting?.eInvoiceRatePerPcs;
        form.setFieldValue("ratePerPcs", rate);
      }
      form.setFieldValue(
        "amt",
        Number(allValues?.count || 0) * Number(allValues?.ratePerPcs || 0)
      );
    },
    [form, dispatch, settings]
  );

  // ── Shared filter for Select search ───────────────────────────────────────
  const filterOption = (input, option) =>
    (option?.children ?? "").toLowerCase().includes(input.toLowerCase());

  const formItemLayout = { labelCol: { span: 24 }, wrapperCol: { span: 24 } };

  return (
    <Modal
      open={visible}
      title={record ? "Update Item" : "Add Item"}
      okText="Submit"
      cancelText="Cancel"
      width={800}
      centered
      bodyStyle={{ paddingTop: 20 }}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            if (record?.id) delete values.isForAll;
            onCreate(values);
            form.resetFields();
            onCancel();
          })
          .catch((info) => console.log("Validate Failed:", info));
      }}
      zIndex={9999}
    >
      <Form
        {...formItemLayout}
        form={form}
        layout="vertical"
        name="item_form"
        onValuesChange={handleValuesChange}
      >
        <Row gutter={24}>

          {/* ── Name ─────────────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Please enter the name!" }]}
            >
              <Input placeholder="Enter name" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* ── Series dropdown ──────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item
              name="seriesId"
              label="Series"
            >
              <Select
                placeholder="Select Series"
                style={{ width: "100%" }}
                allowClear
                showSearch
                filterOption={filterOption}
              >
                {seriesList.map((s) => (
                  <Select.Option key={s.id} value={s.id}>
                    {s.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* ── Category dropdown ────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item
              name="categoryid"
              label="Category"
            >
              <Select
                placeholder="Select Category"
                style={{ width: "100%" }}
                allowClear
                showSearch
                filterOption={filterOption}
              >
                {categories.map((c) => (
                  <Select.Option key={c.id} value={c.id}>
                    {c.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* ── Color dropdown ───────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item
              name="colorId"
              label="Color"
            >
              <Select
                placeholder="Select Color"
                style={{ width: "100%" }}
                allowClear
                showSearch
                filterOption={filterOption}
              >
                {colors.map((c) => (
                  <Select.Option key={c.id} value={c.id}>
                    {c.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* ── Weight ───────────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item
              name="weight"
              label="Weight"
              rules={[{ type: "number", message: "Must be a valid Weight!" }]}
            >
              <InputNumber placeholder="Enter weight" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* ── Length ───────────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item
              name="length"
              label="Length"
              rules={[{ type: "number", message: "Must be a valid Length!" }]}
            >
              <InputNumber placeholder="Enter length" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* ── Height ───────────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item
              name="height"
              label="Height"
              rules={[{ type: "number", message: "Must be a valid Height!" }]}
            >
              <InputNumber placeholder="Enter height" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* ── Display ──────────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item name="display" label="Display">
              <Input placeholder="Enter display" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* ── Display Type ─────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item name="displayType" label="Display Type">
              <Input placeholder="Enter Display Type" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* ── Front Camera ─────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item name="frontCamera" label="Front Camera">
              <Input placeholder="Enter Front Camera" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* ── Rear Camera ──────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item name="rearCamera" label="Rear Camera">
              <Input placeholder="Enter Rear Camera" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* ── Processor ────────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item name="processor" label="Processor">
              <Input placeholder="Enter Processor" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* ── MM Size ──────────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item name="mmSize" label="MM Size">
              <Input placeholder="Enter MM Size" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* ── RAM ──────────────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item name="ram" label="Ram">
              <Select
                placeholder="Select Ram"
                style={{ width: "100%" }}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {RAM_TYPE?.map((c) => (
                  <Select.Option key={c.id} value={c.id}>
                    {c.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* ── Storage ──────────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item name="storage" label="Storage">
              <Select
                placeholder="Select Storage"
                style={{ width: "100%" }}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {STORAGE_TYPE?.map((c) => (
                  <Select.Option key={c.id} value={c.id}>
                    {c.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* ── Battery ──────────────────────────────────────────────────── */}
          <Col span={8}>
            <Form.Item
              name="battery"
              label="Battery"
              rules={[{ type: "number", message: "Must be a valid Battery!" }]}
            >
              <InputNumber placeholder="Enter Battery" style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* ── isForAll (add only) ───────────────────────────────────────── */}
          {!record?.id && (
            <Col span={8}>
              <Form.Item
                name="isForAll"
                label={
                  <span>
                    Add For All{" "}
                    <Tooltip title="If enabled, this item will be added for all database records.">
                      <InfoCircleOutlined style={{ marginLeft: 4, color: "#1890ff" }} />
                    </Tooltip>
                  </span>
                }
              >
                <Switch />
              </Form.Item>
            </Col>
          )}

        </Row>
      </Form>
    </Modal>
  );
};

export default ItemForm;